import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { getOnboardingPathForTier } from '../lib/tier';
import { takeOAuthRedirect } from '../lib/oauthRedirect';

/** /login?error=oauth_failed, carrying a (safe) deep link back for the next attempt. */
function failedLoginPath(back: string | null): string {
  return back ? `/login?error=oauth_failed&redirect=${encodeURIComponent(back)}` : '/login?error=oauth_failed';
}

export default function OAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [error, setError] = useState<string | null>(null);

  // Track which one-time exchange code has already been processed. Without
  // this, an effect re-fire (StrictMode in dev, or any context re-render
  // that re-evaluates the dep array) re-POSTs the same code, gets 401 from
  // the consume-once Map on the backend, and the .catch then schedules a
  // navigate('/login?error=oauth_failed') 2s later — bouncing the user OUT
  // of the onboarding wizard the first effect run had successfully sent
  // them to. Idempotency by code is the cheapest, most defensible fix.
  const processedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const code = searchParams.get('code');

    if (!code) {
      const back = takeOAuthRedirect();
      queueMicrotask(() => {
        if (!cancelled) setError('Authentication failed. Please try again.');
      });
      setTimeout(() => {
        if (!cancelled) navigate(failedLoginPath(back));
      }, 2000);
      return () => { cancelled = true; };
    }

    if (processedCodeRef.current === code) {
      // Same code already processed by an earlier render of this effect;
      // do not re-POST. The first run is in flight (or completed) and has
      // navigated or will navigate — second run would only corrupt that.
      return () => { cancelled = true; };
    }
    processedCodeRef.current = code;

    apiFetch<{
      token: string;
      user: { id: string; email: string; name: string };
      isNew: boolean;
      isNewTenant: boolean;
      subscriptionTier?: string | null;
    }>('/auth/oauth/exchange', {
      method: 'POST',
      body: JSON.stringify({ code }),
    })
      .then(({ token, user, isNewTenant, subscriptionTier }) => {
        if (cancelled) return;
        // #3248 PR 2 — the deep link stored by OAuthButtons (safe or null; cleared either way).
        const back = takeOAuthRedirect();
        setSession(token, user);
        // #1997 — branch on isNewTenant, not isNew. A brand-new OAuth SIGNUP
        // still only needs the tier-appropriate onboarding wizard when a
        // brand-new TENANT was actually minted (the personal-tenant
        // fallback path in oauth.strategies.ts). A new user who instead
        // consumed a pending invitation joined an EXISTING tenant —
        // isNew is true but isNewTenant is false, and they must land on
        // /projects like any other member, not the tenant-creation wizard
        // (which for an academic invite would send them to
        // /onboarding/academic-admin — the wrong flow entirely for a
        // student joining, not an admin provisioning).
        if (isNewTenant) {
          // A brand-new tenant still sets up first; the deep link is dropped.
          navigate(getOnboardingPathForTier(subscriptionTier), { replace: true });
        } else {
          navigate(back ?? '/projects', { replace: true });
        }
      })
      .catch(() => {
        if (cancelled) return;
        const back = takeOAuthRedirect();
        setError('Authentication failed. Please try again.');
        setTimeout(() => navigate(failedLoginPath(back)), 2000);
      });

    return () => { cancelled = true; };
  }, [searchParams, navigate, setSession]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <p style={{ color: 'var(--error)', fontFamily: 'var(--font-body)' }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
      <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>
        Completing sign in...
      </p>
    </div>
  );
}
